/**
 * GFG-405 — Add gift links, step 1 (prototype `cxLinkQty*`): HOW MANY LINKS? with − / number / +
 * and the cost line under it. Controlled: the typed text is the wizard's (`qtyText`), so typing is
 * not clamped mid-entry (an empty box stays editable) and blur snaps it into 1..10,000.
 */
import { LINK_QTY_MAX, LINK_QTY_MIN } from "@giftforward/naming";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useId } from "react";
import { GIFT_LINKS_COPY } from "@/constants/giftLinks";

interface GiftLinksQuantityStepProps {
  /** The field as typed. */
  qtyText: string;
  /** "[F into free space(s) · ]P × $X = $T" (`giftLinksCostLine`). */
  costLine: string;
  disabled: boolean;
  onType: (text: string) => void;
  onBlur: () => void;
  onStep: (delta: 1 | -1) => void;
}

const COPY = GIFT_LINKS_COPY.wizard;

const SECTION_CLASS = "mb-2.5 block text-[11px] font-bold uppercase tracking-[2px] text-dust-ink";

/** Prototype `.cs-iconbtn`. */
const STEP_BUTTON_CLASS =
  "inline-flex h-[34px] w-[34px] shrink-0 cursor-pointer items-center justify-center rounded-lg " +
  "border border-smoke bg-white text-graphite transition-colors hover:border-dark-dust " +
  "hover:text-rodeo-black disabled:cursor-default disabled:opacity-40";

/** Prototype `.cx-qty input`. */
const INPUT_CLASS =
  "mx-2 h-[34px] w-[110px] rounded-lg border border-smoke bg-white text-center font-lato " +
  "text-[15px] text-rodeo-black focus:border-dark-dust focus:outline-none disabled:opacity-60";

/** − or +: one step down or up; held at its end of the range. */
function StepButton({
  delta,
  disabled,
  onStep,
}: {
  delta: 1 | -1;
  disabled: boolean;
  onStep: (delta: 1 | -1) => void;
}) {
  const Icon = delta === 1 ? AddIcon : RemoveIcon;
  return (
    <button
      type="button"
      aria-label={delta === 1 ? COPY.stepper.more : COPY.stepper.fewer}
      disabled={disabled}
      onClick={() => onStep(delta)}
      className={STEP_BUTTON_CLASS}
    >
      <Icon sx={{ fontSize: 18 }} />
    </button>
  );
}

export default function GiftLinksQuantityStep({
  qtyText,
  costLine,
  disabled,
  onType,
  onBlur,
  onStep,
}: GiftLinksQuantityStepProps) {
  const inputId = useId();
  const costId = useId();
  // Only to hold a button at its end of the range; the step itself clamps (`stepLinkQty`).
  const typed = Number.parseInt(qtyText, 10);

  return (
    <div>
      <label htmlFor={inputId} className={SECTION_CLASS}>
        {COPY.quantityLabel}
      </label>
      <div className="flex items-center">
        <StepButton delta={-1} disabled={disabled || typed <= LINK_QTY_MIN} onStep={onStep} />
        <input
          id={inputId}
          type="number"
          inputMode="numeric"
          min={LINK_QTY_MIN}
          max={LINK_QTY_MAX}
          step={1}
          value={qtyText}
          aria-label={COPY.stepper.input}
          aria-describedby={costId}
          disabled={disabled}
          onChange={(event) => onType(event.target.value)}
          onBlur={onBlur}
          className={INPUT_CLASS}
        />
        <StepButton delta={1} disabled={disabled || typed >= LINK_QTY_MAX} onStep={onStep} />
      </div>
      <p id={costId} aria-live="polite" className="mt-3 text-[13.5px] text-graphite">
        {costLine}
      </p>
    </div>
  );
}
